import { useEffect, useRef, useState } from "react";

interface TargetDef {
  token: string;
  fakes: string[];
}

const TARGET_POOL: TargetDef[] = [
  { token: "RED/09", fakes: ["RED/90", "RED/O9", "RE0/09", "RED/0G"] },
  { token: "BLUE/11", fakes: ["BLUE/1I", "BLUE/I1", "BLUE/LL", "BIUE/11"] },
  { token: "GREEN/77", fakes: ["GREEN/7T", "GREEN/T7", "GR3EN/77", "GREEN/T1"] },
  { token: "GOLD/05", fakes: ["G0LD/05", "GOLD/50", "GOLD/O5", "GOLD/06"] },
];

const NORMAL_TOKENS = [
  "AA/11", "BB/22", "CC/33", "ZX/88", "QW/77",
  "LK/92", "TR/45", "XY/30", "PD/14", "MN/63",
];

const SESSION_TIME = 60; // seconds
const MEMORIZE_TIME = 3500; // ms

const ACHIEVEMENTS_LIST: Record<string, { label: string; desc: string }> = {
  first_session: { label: "First Focus Session", desc: "Complete your first training session" },
  ten_hits: { label: "10 Hits", desc: "Land 10 correct hits across all sessions" },
  fifty_hits: { label: "50 Hits", desc: "Land 50 correct hits across all sessions" },
  hundred_hits: { label: "100 Hits", desc: "Land 100 correct hits across all sessions" },
  level_10: { label: "Level 10", desc: "Reach level 10 in a single session" },
  level_25: { label: "Level 25", desc: "Reach level 25 in a single session" },
  perfect_accuracy: { label: "Perfect Accuracy", desc: "Finish a session with 100% accuracy" },
  focus_master: { label: "Focus Master", desc: "Finish a session at full focus" },
};

interface Stats {
  highestScore: number;
  bestAccuracy: number;
  highestLevel: number;
  bestReactionTime: number | null;
  longestStreak: number;
  totalSessions: number;
  totalHits: number;
}

const DEFAULT_STATS: Stats = {
  highestScore: 0,
  bestAccuracy: 0,
  highestLevel: 1,
  bestReactionTime: null,
  longestStreak: 0,
  totalSessions: 0,
  totalHits: 0,
};

function loadStats(): Stats {
  try {
    const raw = localStorage.getItem("focusGameStats");
    if (!raw) return DEFAULT_STATS;
    return { ...DEFAULT_STATS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_STATS;
  }
}

function loadAchievements(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem("focusGameAchievements");
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function getSpeedForLevel(level: number) {
  const points = [
    { level: 1, speed: 1000 },
    { level: 5, speed: 800 },
    { level: 10, speed: 600 },
    { level: 20, speed: 400 },
    { level: 30, speed: 250 },
  ];

  if (level <= points[0].level) return points[0].speed;
  if (level >= points[points.length - 1].level) return points[points.length - 1].speed;

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    if (level >= a.level && level <= b.level) {
      const ratio = (level - a.level) / (b.level - a.level);
      return Math.round(a.speed + (b.speed - a.speed) * ratio);
    }
  }
  return 1000;
}

function generateToken(target: TargetDef, level: number) {
  const r = Math.random();
  const fakeChance = Math.min(0.15 + level * 0.01, 0.4);

  if (r < 0.22) {
    return { tok: target.token, isTarget: true };
  }
  if (r < 0.22 + fakeChance) {
    const fake = target.fakes[Math.floor(Math.random() * target.fakes.length)];
    return { tok: fake, isTarget: false };
  }
  const tok = NORMAL_TOKENS[Math.floor(Math.random() * NORMAL_TOKENS.length)];
  return { tok, isTarget: false };
}

export default function FocusGame() {
  // ===================== UI STATE =====================
  const [phase, setPhase] = useState<"idle" | "memorize" | "playing" | "ended">("idle");
  const [token, setToken] = useState("READY");
  const [isTarget, setIsTarget] = useState(false);
  const [currentTarget, setCurrentTarget] = useState<TargetDef>(TARGET_POOL[0]);

  const [score, setScore] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);

  const [timeLeft, setTimeLeft] = useState(SESSION_TIME);

  const [level, setLevel] = useState(1);
  const [xp, setXp] = useState(0);

  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  const [focus, setFocus] = useState(100);
  const [lives, setLives] = useState(3);

  const [stream, setStream] = useState<string[]>([]);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);

  const [ruleMessage, setRuleMessage] = useState("");
  const [endReason, setEndReason] = useState<"time" | "lives" | "focus" | null>(null);

  const [stats, setStats] = useState<Stats>(loadStats);
  const [achievements, setAchievements] = useState<Record<string, boolean>>(loadAchievements);
  const [achievementToast, setAchievementToast] = useState("");

  // ===================== REFS (source of truth during loop) =====================
  const targetRef = useRef<TargetDef>(TARGET_POOL[0]);
  const levelRef = useRef(1);
  const xpRef = useRef(0);
  const scoreRef = useRef(0);
  const hitsRef = useRef(0);
  const missesRef = useRef(0);
  const missedTargetsRef = useRef(0);
  const focusRef = useRef(100);
  const livesRef = useRef(3);
  const streakRef = useRef(0);
  const bestStreakRef = useRef(0);

  const isTargetRef = useRef(false);
  const hitRef = useRef(false);
  const tokenStartRef = useRef(0);
  const tokenCountRef = useRef(0);
  const runningRef = useRef(false);

  const tokenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ruleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const achievementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ===================== HELPERS =====================
  const syncDisplay = () => {
    setScore(scoreRef.current);
    setHits(hitsRef.current);
    setMisses(missesRef.current);
    setMissedTargets(missedTargetsRef.current);
    setFocus(focusRef.current);
    setLives(livesRef.current);
    setCurrentStreak(streakRef.current);
    setBestStreak(bestStreakRef.current);
    setXp(xpRef.current);
    setLevel(levelRef.current);
  };

  const unlockAchievement = (key: string) => {
    setAchievements((prev) => {
      if (prev[key]) return prev;
      const updated = { ...prev, [key]: true };
      localStorage.setItem("focusGameAchievements", JSON.stringify(updated));

      setAchievementToast(`Achievement unlocked: ${ACHIEVEMENTS_LIST[key].label}`);
      if (achievementTimeoutRef.current) clearTimeout(achievementTimeoutRef.current);
      achievementTimeoutRef.current = setTimeout(() => setAchievementToast(""), 3500);

      return updated;
    });
  };

  const endGame = (reason: "time" | "lives" | "focus") => {
    runningRef.current = false;
    if (tokenTimeoutRef.current) clearTimeout(tokenTimeoutRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (ruleTimeoutRef.current) clearTimeout(ruleTimeoutRef.current);
    syncDisplay();
    setEndReason(reason);
    setPhase("ended");
  };

  // ===================== TOKEN LOOP =====================
  const scheduleNextToken = () => {
    const speed = getSpeedForLevel(levelRef.current);
    tokenTimeoutRef.current = setTimeout(tick, speed);
  };

  const tick = () => {
    if (!runningRef.current) return;

    // Penalize if previous token was a real target and player missed it
    if (isTargetRef.current && !hitRef.current) {
      missesRef.current += 1;
      missedTargetsRef.current += 1;
      streakRef.current = 0;
      focusRef.current = Math.max(0, focusRef.current - 15);
      syncDisplay();

      if (focusRef.current <= 0) {
        endGame("focus");
        return;
      }
    }

    tokenCountRef.current += 1;

    // Occasional rule change
    if (levelRef.current >= 3 && tokenCountRef.current % 14 === 0) {
      const others = TARGET_POOL.filter((t) => t.token !== targetRef.current.token);
      const newTarget = others[Math.floor(Math.random() * others.length)];
      const oldToken = targetRef.current.token;

      targetRef.current = newTarget;
      setCurrentTarget(newTarget);
      setRuleMessage(`Rule updated — the new target is ${newTarget.token} (previously ${oldToken})`);

      if (ruleTimeoutRef.current) clearTimeout(ruleTimeoutRef.current);
      ruleTimeoutRef.current = setTimeout(() => setRuleMessage(""), 4000);
    }

    const { tok, isTarget: targetFlag } = generateToken(targetRef.current, levelRef.current);
    isTargetRef.current = targetFlag;
    hitRef.current = false;
    tokenStartRef.current = Date.now();

    setToken(tok);
    setIsTarget(targetFlag);
    setStream((prev) => [tok, ...prev.slice(0, 7)]);

    scheduleNextToken();
  };

  // ===================== START / ACTIONS =====================
  const startGame = () => {
    if (runningRef.current) return;

    scoreRef.current = 0;
    hitsRef.current = 0;
    missesRef.current = 0;
    missedTargetsRef.current = 0;
    focusRef.current = 100;
    livesRef.current = 3;
    streakRef.current = 0;
    bestStreakRef.current = 0;
    xpRef.current = 0;
    levelRef.current = 1;
    isTargetRef.current = false;
    hitRef.current = false;
    tokenCountRef.current = 0;

    const initialTarget = TARGET_POOL[Math.floor(Math.random() * TARGET_POOL.length)];
    targetRef.current = initialTarget;
    setCurrentTarget(initialTarget);

    setReactionTimes([]);
    setStream([]);
    setRuleMessage("");
    setEndReason(null);
    setToken("READY");
    setIsTarget(false);
    setTimeLeft(SESSION_TIME);
    syncDisplay();
    setPhase("memorize");

    setTimeout(() => {
      setPhase("playing");
      runningRef.current = true;
      tick();

      timerIntervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            endGame("time");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, MEMORIZE_TIME);
  };

  const pulseShield = () => {
    if (!runningRef.current) return;

    const reaction = Date.now() - tokenStartRef.current;
    setReactionTimes((prev) => [...prev, reaction]);

    if (isTargetRef.current) {
      hitRef.current = true;
      hitsRef.current += 1;

      streakRef.current += 1;
      if (streakRef.current > bestStreakRef.current) bestStreakRef.current = streakRef.current;

      const multiplier =
        streakRef.current >= 10 ? 5 :
        streakRef.current >= 5 ? 3 :
        streakRef.current >= 3 ? 2 : 1;

      scoreRef.current += 10 * multiplier;

      let newXp = xpRef.current + 10 * multiplier;
      let newLevel = levelRef.current;
      let needed = newLevel * 100;

      while (newXp >= needed) {
        newXp -= needed;
        newLevel += 1;
        needed = newLevel * 100;
      }

      xpRef.current = newXp;
      if (newLevel !== levelRef.current) {
        levelRef.current = newLevel;
        if (newLevel >= 10) unlockAchievement("level_10");
        if (newLevel >= 25) unlockAchievement("level_25");
      }

      focusRef.current = Math.min(100, focusRef.current + 5);
      syncDisplay();
    } else {
      missesRef.current += 1;
      streakRef.current = 0;

      focusRef.current = Math.max(0, focusRef.current - 10);
      scoreRef.current = Math.max(0, scoreRef.current - 5);
      livesRef.current -= 1;

      syncDisplay();

      if (focusRef.current <= 0) {
        endGame("focus");
        return;
      }
      if (livesRef.current <= 0) {
        endGame("lives");
        return;
      }
    }
  };

  // ===================== SESSION END: PERSIST STATS + ACHIEVEMENTS =====================
  useEffect(() => {
    if (phase !== "ended") return;

    const sessionAccuracy = hits + misses === 0 ? 0 : Math.round((hits / (hits + misses)) * 100);
    const avgReaction = reactionTimes.length
      ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
      : null;

    setStats((prev) => {
      const updated: Stats = {
        highestScore: Math.max(prev.highestScore, score),
        bestAccuracy: Math.max(prev.bestAccuracy, sessionAccuracy),
        highestLevel: Math.max(prev.highestLevel, level),
        bestReactionTime:
          avgReaction != null
            ? prev.bestReactionTime == null
              ? avgReaction
              : Math.min(prev.bestReactionTime, avgReaction)
            : prev.bestReactionTime,
        longestStreak: Math.max(prev.longestStreak, bestStreak),
        totalSessions: prev.totalSessions + 1,
        totalHits: prev.totalHits + hits,
      };

      localStorage.setItem("focusGameStats", JSON.stringify(updated));

      if (updated.totalSessions === 1) unlockAchievement("first_session");
      if (updated.totalHits >= 10) unlockAchievement("ten_hits");
      if (updated.totalHits >= 50) unlockAchievement("fifty_hits");
      if (updated.totalHits >= 100) unlockAchievement("hundred_hits");
      if (sessionAccuracy === 100 && hits > 0) unlockAchievement("perfect_accuracy");
      if (focus >= 100) unlockAchievement("focus_master");

      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ===================== CLEANUP =====================
  useEffect(() => {
    return () => {
      if (tokenTimeoutRef.current) clearTimeout(tokenTimeoutRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (ruleTimeoutRef.current) clearTimeout(ruleTimeoutRef.current);
      if (achievementTimeoutRef.current) clearTimeout(achievementTimeoutRef.current);
    };
  }, []);

  // ===================== DERIVED VALUES =====================
  const accuracy = hits + misses === 0 ? 100 : Math.round((hits / (hits + misses)) * 100);
  const avgReaction = reactionTimes.length
    ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
    : 0;
  const fastestReaction = reactionTimes.length ? Math.min(...reactionTimes) : 0;
  const slowestReaction = reactionTimes.length ? Math.max(...reactionTimes) : 0;

  const xpNeeded = level * 100;
  const xpProgress = xpNeeded > 0 ? Math.min(100, Math.round((xp / xpNeeded) * 100)) : 0;

  const streakMultiplier =
    currentStreak >= 10 ? 5 :
    currentStreak >= 5 ? 3 :
    currentStreak >= 3 ? 2 : 1;

  const focusRating =
    focus >= 80 ? "Excellent" :
    focus >= 60 ? "Good" :
    focus >= 40 ? "Fair" : "Needs improvement";

  const endReasonText =
    endReason === "time" ? "Time's up — session complete!" :
    endReason === "lives" ? "Out of lives — session ended." :
    endReason === "focus" ? "Focus depleted — session ended." : "";

  const running = phase === "playing";

  // ===================== RENDER =====================
  return (
    <div className="min-h-screen bg-[#FAF6EF] p-4 md:p-6 text-stone-800">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Achievement Toast */}
        {achievementToast && (
          <div className="bg-amber-100 border border-amber-300 text-amber-800 rounded-2xl p-3 text-center font-semibold shadow-sm transition-all">
            🏆 {achievementToast}
          </div>
        )}

        {/* HEADER */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-stone-800">
              Attention Training
            </h1>
            <p className="text-stone-500 mt-1">
              Focus Session · Cognitive Performance Lab
            </p>
          </div>

          <button
            onClick={startGame}
            disabled={running || phase === "memorize"}
            className="px-6 py-3 rounded-xl bg-amber-700 text-white font-semibold hover:bg-amber-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {running ? "Focus Session Active" : phase === "memorize" ? "Get Ready..." : "Start Focus Session"}
          </button>
        </div>

        {/* CORE STATS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">Score</p>
            <h2 className="text-2xl font-bold text-stone-800">{score}</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">Accuracy</p>
            <h2 className="text-2xl font-bold text-stone-800">{accuracy}%</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">Lives</p>
            <h2 className="text-2xl font-bold text-rose-500">
              {"❤️".repeat(Math.max(lives, 0)) || "—"}
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">Time Left</p>
            <h2 className="text-2xl font-bold text-amber-700">{timeLeft}s</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">Streak</p>
            <h2 className="text-2xl font-bold text-stone-800">
              {currentStreak} <span className="text-sm text-amber-700">x{streakMultiplier}</span>
            </h2>
            <p className="text-xs text-stone-400 mt-1">Best: {bestStreak}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-4">
            <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">Level</p>
            <h2 className="text-2xl font-bold text-stone-800">{level}</h2>
          </div>
        </div>

        {/* PROGRESS BARS: XP + FOCUS */}
        <div className="grid md:grid-cols-2 gap-4">

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5">
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-stone-700">Training Progress (XP)</p>
              <p className="text-sm text-stone-400">{xp} / {xpNeeded}</p>
            </div>
            <div className="w-full h-3 bg-amber-50 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-600 rounded-full transition-all duration-300"
                style={{ width: `${xpProgress}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5">
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-stone-700">Focus Meter</p>
              <p className="text-sm text-stone-400">{focus}%</p>
            </div>
            <div className="w-full h-3 bg-rose-50 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  focus > 50 ? "bg-emerald-500" : focus > 20 ? "bg-amber-500" : "bg-rose-500"
                }`}
                style={{ width: `${focus}%` }}
              />
            </div>
          </div>
        </div>

        {/* RULE UPDATE BANNER */}
        {ruleMessage && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-3 text-center font-medium">
            🔄 {ruleMessage}
          </div>
        )}

        {/* GAME AREA */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8">

          {phase === "idle" && (
            <div className="text-center py-10">
              <h3 className="text-xl font-semibold text-stone-700 mb-2">
                Ready to train your attention?
              </h3>
              <p className="text-stone-500 max-w-md mx-auto">
                A target token will appear before each session. Watch the stream carefully and
                tap "Pulse Shield" only when the exact target token appears — fake look-alikes
                are designed to test your concentration.
              </p>
            </div>
          )}

          {phase === "memorize" && (
            <div className="text-center py-8">
              <p className="text-stone-500 mb-3 uppercase tracking-wide text-sm">
                Memorize the target token
              </p>
              <div className="text-5xl md:text-6xl font-black text-amber-700 mb-4">
                {currentTarget.token}
              </div>
              <p className="text-stone-500 max-w-md mx-auto">
                Tap "Pulse Shield" ONLY when <span className="font-semibold text-amber-700">{currentTarget.token}</span> appears.
                Watch out for similar-looking fakes like{" "}
                <span className="font-mono text-rose-400">{currentTarget.fakes[0]}</span>.
              </p>
            </div>
          )}

          {(phase === "playing" || phase === "ended") && (
            <div className="flex flex-col items-center justify-center py-8">

              <p className="text-stone-400 text-sm uppercase tracking-wide mb-2">
                Current target: <span className="font-semibold text-amber-700">{currentTarget.token}</span>
              </p>

              <div
                className={`text-6xl md:text-7xl font-black tracking-widest transition-all duration-300 ${
                  isTarget && running ? "text-amber-700 scale-110" : "text-stone-700"
                }`}
              >
                {phase === "ended" ? "—" : token}
              </div>

              <p className="text-stone-400 mt-4 text-center max-w-sm">
                Press Pulse Shield only when the current target token appears on screen.
              </p>

              <button
                onClick={pulseShield}
                disabled={!running}
                className="mt-8 px-10 py-4 rounded-2xl bg-amber-700 text-white font-bold text-lg hover:bg-amber-800 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-sm active:scale-95"
              >
                Pulse Shield
              </button>
            </div>
          )}
        </div>

        {/* STREAM + PERFORMANCE DASHBOARD */}
        <div className="grid md:grid-cols-2 gap-4">

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5">
            <h3 className="font-semibold text-stone-700 mb-3">Live Token Stream</h3>
            <div className="h-40 overflow-auto font-mono text-sm space-y-1 bg-[#FAF6EF] rounded-xl p-3">
              {stream.length === 0 ? (
                <p className="text-stone-400">No tokens yet — start a session.</p>
              ) : (
                stream.map((item, index) => (
                  <div key={index} className="text-stone-600">
                    {item}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5">
            <h3 className="font-semibold text-stone-700 mb-3">Performance Report</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#FAF6EF] rounded-xl p-3">
                <p className="text-stone-400">Hits</p>
                <p className="font-bold text-emerald-600 text-lg">{hits}</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-3">
                <p className="text-stone-400">Misses</p>
                <p className="font-bold text-rose-500 text-lg">{misses}</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-3">
                <p className="text-stone-400">Missed Targets</p>
                <p className="font-bold text-stone-700 text-lg">{missedTargets}</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-3">
                <p className="text-stone-400">Focus Accuracy</p>
                <p className="font-bold text-stone-700 text-lg">{accuracy}%</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-3">
                <p className="text-stone-400">Avg Reaction</p>
                <p className="font-bold text-stone-700 text-lg">{avgReaction} ms</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-3">
                <p className="text-stone-400">Fastest / Slowest</p>
                <p className="font-bold text-stone-700 text-lg">
                  {fastestReaction} / {slowestReaction} ms
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SESSION SUMMARY */}
        {phase === "ended" && (
          <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-amber-700 mb-2">
              Session Summary
            </h2>
            <p className="text-stone-500 mb-6">{endReasonText}</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
              <div className="bg-[#FAF6EF] rounded-xl p-4">
                <p className="text-stone-400 text-sm">Final Score</p>
                <p className="text-xl font-bold text-stone-800">{score}</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-4">
                <p className="text-stone-400 text-sm">Accuracy</p>
                <p className="text-xl font-bold text-stone-800">{accuracy}%</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-4">
                <p className="text-stone-400 text-sm">Level Reached</p>
                <p className="text-xl font-bold text-stone-800">{level}</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-4">
                <p className="text-stone-400 text-sm">Avg Reaction</p>
                <p className="text-xl font-bold text-stone-800">{avgReaction} ms</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-4">
                <p className="text-stone-400 text-sm">Best Streak</p>
                <p className="text-xl font-bold text-stone-800">{bestStreak}</p>
              </div>
              <div className="bg-[#FAF6EF] rounded-xl p-4">
                <p className="text-stone-400 text-sm">Focus Rating</p>
                <p className="text-xl font-bold text-stone-800">{focusRating}</p>
              </div>
            </div>

            <button
              onClick={startGame}
              className="mt-6 px-8 py-3 rounded-xl bg-amber-700 text-white font-semibold hover:bg-amber-800 transition shadow-sm"
            >
              Start New Session
            </button>
          </div>
        )}

        {/* ACHIEVEMENTS */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5">
          <h3 className="font-semibold text-stone-700 mb-3">Achievements</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ACHIEVEMENTS_LIST).map(([key, info]) => (
              <span
                key={key}
                title={info.desc}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  achievements[key]
                    ? "bg-amber-600 text-white"
                    : "bg-stone-100 text-stone-400"
                }`}
              >
                🏆 {info.label}
              </span>
            ))}
          </div>
        </div>

        {/* ALL-TIME STATS */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-5">
          <h3 className="font-semibold text-stone-700 mb-3">All-Time Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-[#FAF6EF] rounded-xl p-3">
              <p className="text-stone-400">Highest Score</p>
              <p className="font-bold text-stone-800 text-lg">{stats.highestScore}</p>
            </div>
            <div className="bg-[#FAF6EF] rounded-xl p-3">
              <p className="text-stone-400">Best Accuracy</p>
              <p className="font-bold text-stone-800 text-lg">{stats.bestAccuracy}%</p>
            </div>
            <div className="bg-[#FAF6EF] rounded-xl p-3">
              <p className="text-stone-400">Highest Level</p>
              <p className="font-bold text-stone-800 text-lg">{stats.highestLevel}</p>
            </div>
            <div className="bg-[#FAF6EF] rounded-xl p-3">
              <p className="text-stone-400">Best Reaction Time</p>
              <p className="font-bold text-stone-800 text-lg">
                {stats.bestReactionTime != null ? `${stats.bestReactionTime} ms` : "—"}
              </p>
            </div>
            <div className="bg-[#FAF6EF] rounded-xl p-3">
              <p className="text-stone-400">Longest Streak</p>
              <p className="font-bold text-stone-800 text-lg">{stats.longestStreak}</p>
            </div>
            <div className="bg-[#FAF6EF] rounded-xl p-3">
              <p className="text-stone-400">Total Sessions</p>
              <p className="font-bold text-stone-800 text-lg">{stats.totalSessions}</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}